import React, { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

// Import Scrollbar
import SimpleBar from "simplebar-react";

import withRouter from "components/Common/withRouter";
import { Link, useNavigate } from "react-router-dom";

// i18n
import { withTranslation } from "react-i18next";

// Import fungsi untuk mendapatkan level pengguna
import { getUserLevel, isAkunMandor } from '../../helpers/auth'; 

// Service untuk badge notifikasi jumlah data "belum di-approve"
import {
  getTotalAjukanKeuangan,
  getTotalPelunasanDP,
  getTotalPencairanKPR,
} from '../../services/masterUsersService';
import { fetchInbox, fetchPengajuans } from '../../services/pengajuanService';
import { fetchPendingProdukRumah } from '../../services/produkRumahService';

// ==========================================================
// 🎨 DEFINISI VARIABEL GLOBAL (HARUS DI LUAR KOMPONEN) 🎨
// ==========================================================

const COLOR_SCHEME = {
    // Layout & Background
    BG_DEFAULT: '#FFFFFF',          
    TEXT_DEFAULT: '#343a40',        
    
    // Hierarchy & Emphasis
    MENU_TITLE: '#6c757d',          
    
    // Active/Focus State
    PRIMARY_COLOR: '#0d6efd',       
    ACTIVE_BG: '#eaf4ff',           
    ACTIVE_TEXT: '#0d6efd',         
    SHADOW_COLOR: 'rgba(13, 110, 253, 0.1)', 
    
    // Icons
    ICON_COLOR: '#6c757d',          
    SUB_MENU_BG: '#f8f9fa',

    // Danger (Logout)
    DANGER_TEXT: '#dc3545',
    DANGER_BG: '#f8d7da',

    // Badge notifikasi
    BADGE_BG: '#dc3545',
    BADGE_TEXT: '#ffffff',
};

// ==========================================================
// 🚨 DEFINISI MENU MENGGUNAKAN NAMA DIVISI (STRING) 🚨
// ==========================================================

const MENU_ITEMS = [
    {
      label: "Beranda",
      icon: "bx bx-home-circle",
      path: "/dashboard"
    },
     {
      label: "Absensi",
      icon: "bx bx-check-circle",
      path: "/absensi"
    },
    // Input Quality Control — hanya untuk pengawas yang memeriksa di lapangan.
    // "Quality Kontrol" adalah nama jabatan di master jabatan (dengan K),
    // dipakai bersama Pengawas Cabang. ADMIN sengaja tidak ditampilkan lagi.
    {
      label: "Quality Control",
      icon: "bx bx-check-shield",
      path: "/quality-control",
      roles: ["Pengawas Cabang", "Quality Kontrol"]
    },
    // Upload Serah Terima Kunci — pengawas lapangan; ADMIN untuk koreksi.
    {
      label: "Upload Serah Terima Kunci",
      icon: "bx bx-key",
      path: "/serah-terima-kunci",
      roles: ["Pengawas Cabang", "ADMIN"]
    },
    // Halaman akun mandor. Sengaja memakai `roles` supaya HANYA muncul untuk
    // jabatan "Mandor" — pengguna lain tidak melihatnya (dan akun mandor
    // sendiri tidak melihat menu lain, lihat penyaring di bawah).
    {
      label: "Pembangunan Saya",
      icon: "bx bx-building-house",
      path: "/pembangunan-saya",
      roles: ["Mandor"]
    },
    // --- PEMISAH VISUAL ---
    { isSeparator: true }, 
    // --- MASTER ---
 {
  label: "Master",
  icon: "bx bx-archive",
  isHasArrow: true,
  groupByDivision: true,
  roles: ["ADMIN", "Dirut", "Head","Staff"], 
  allowedNames: ["Keuangan", "HRGA", "Legalitas", "Perencanaan", "Branch","Marketing"],
  subItems: [
    // 1. Jabatan: Hanya ADMIN dan DIREKTUR
    { path: "/master-jabatan", label: "Jabatan", roles: ["ADMIN", "Dirut"] },
    
    // 2. Divisi: Hanya untuk yang berada di Divisi Pusat (atau yang berhak mengelola)
    { 
        path: "/master-divisi", 
        label: "Divisi", 
        roles: ["ADMIN", "Dirut", "Head Keuangan"],
        allowedNames: ["Pusat", "Head Office"] 
    },
    
    
    // 3. Sub-item lainnya: Hanya ADMIN dan DIREKTUR
        { path: "/master-bagan-akun", label: "Bagan Akun", roles: ["ADMIN", "Dirut", "Keuangan"], allowedNames: ["Keuangan"] },
        { path: "/master-tipe-transaksi", label: "Tipe Transaksi", roles: ["ADMIN", "Dirut"] },
        { path: "/mapping-akun", label: "Mapping Akun", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Keuangan"] },
        { path: "/master-wilayah", label: "Wilayah", roles: ["ADMIN", "Dirut"] },
        { path: "/mapping-wilayah", label: "Mapping Wilayah", roles: ["ADMIN", "Dirut"] },
        { path: "/master-lokasi", label: "Lokasi Perumahan", roles: ["ADMIN", "Dirut", "Head"], allowedNames: ["Perencanaan"] },
        { path: "/master-blok", label: "Blok Perumahan", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Perencanaan"] },
        { path: "/master-hpp", label: "HPP", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Branch"] },
        { path: "/master-workflow-approval", label: "Alur Approval", roles: ["ADMIN", "Dirut"] },
        { path: "/master-karyawan", label: "Karyawan", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["HRGA"] },
        { path: "/master-pihak-ketiga", label: "Pihak Ketiga", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["HRGA"] },
        { path: "/master-hari-libur", label: "Hari Libur", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["HRGA"] },
        { path: "/pemilik-lahan", label: "Pemilik Lahan", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Legalitas"] },
        { path: "/master-mandor", label: "Mandor", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Branch"] },
        { path: "/master-pembangunan", label: "Pembangunan", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Branch"] },
        { path: "/master-bank", label: "Bank", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Keuangan"] },
        { path: "/master-bank-kpr", label: "Bank KPR", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Marketing"] },
        { path: "/master-notaris", label: "Notaris", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Marketing"] },
        { path: "/pembentukan-modal", label: "Pembentukan Modal", roles: ["ADMIN", "Dirut","Head"], allowedNames: ["Keuangan"] },
        { path: "/pembelian-tanah", label: "Pembelian Tanah", roles: ["ADMIN", "Dirut","Head", "Staff", "Head Keuangan"], allowedNames: ["Legalitas"] },
        { path: "/produk-rumah", label: "Produk Rumah", roles: ["ADMIN", "Dirut","Head", "Staff"], allowedNames: ["Branch","Marketing"], badgeKey: "produk_rumah" },
  ]
},

{
  label: "Monitoring KPR",
  icon: "bx bx-home-circle",
  isHasArrow: true,
  roles: ["Dirut", "Head", "Marketing Officer", "Relationship Officer", "Branch Manager","Branch","Admin Cabang", "Admin Kantor Pusat"], 
  allowedNames: ["Marketing",  "Dirut", "Branch"],
  subItems: [
     {
  label: "Siteplan",
  icon: "bx bx-map-alt",
  path: "/siteplan"
},
    { 
        path: "/unit-dijual", 
        label: "Unit Dijual",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Admin Cabang","Admin Kantor Pusat"],
    },
    { 
        path: "/prospek", 
        label: "Data Prospek",
        allowedNames: ["Marketing",  "Dirut"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Admin Cabang", "Admin Kantor Pusat"],
    },
    { 
        path: "/master-users", 
        label: "Booking Unit",
        allowedNames: ["Marketing",  "Dirut","Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Admin Cabang", "Admin Kantor Pusat"],
    },
    { 
        path: "/data-hold", 
        label: "Data Hold",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer","Admin Cabang", "Admin Kantor Pusat"],
    },
    { 
        path: "/proses-berkas", 
        label: "Proses Berkas",
        allowedNames: ["Marketing",  "Dirut", "Branch", "Admin Cabang"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer", "Admin Cabang", "Admin Kantor Pusat"],
    },
    { 
        path: "/akad-kpr", 
        label: "Akad KPR",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer","Admin Cabang", "Admin Kantor Pusat"],
    },
    { 
        path: "/user-batal", 
        label: "User Batal",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer","Admin Cabang","Admin Kantor Pusat"],
    },
        { 
        path: "/daftar-tunggu", 
        label: "Daftar Tunggu",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer","Admin Cabang","Admin Kantor Pusat"],
    },
       { 
        path: "/laporan-akad-kpr", 
        label: "Laporan Akad KPR",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer","Admin Cabang","Admin Kantor Pusat"],
    },
          { 
        path: "/laporan-penjualan", 
        label: "Laporan Penjualan",
        allowedNames: ["Marketing",  "Dirut", "Branch"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Relationship Officer","Admin Cabang","Admin Kantor Pusat"],
    },
  ]
},

{
  label: "Absensi",
  icon: "bx bx-archive",
  isHasArrow: true,
  roles: ["ADMIN","Dirut","Head"], 
  allowedNames: ["HRGA"],           // tetap di induk
  subItems: [
    { 
      path: "/rekap-absensi", 
      label: "Rekap Absensi",
      allowedNames: ["HRGA"] 
    },
    { 
      path: "/pengajuan-absensi", 
      label: "Pengajuan Absensi",
    },
  ]
},

 {
  label: "Pengajuan",
  icon: "bx bx-archive",
  isHasArrow: true,
  roles: ["Head Keuangan","ADMIN","Dirut", "Head", "Staff","Manager"], 
  subItems: [
    { path: "/pengajuan-transaksi", label: "Transaksi", badgeKey: "transaksi" },
    { 
        path: "/booking-unit", 
        label: "Booking Unit",
        allowedNames: ["Keuangan"],
        badgeKey: "booking_unit", // 🆕 jumlah data "Ajukan ke Keuangan" yang belum di-approve
    },
     { 
        path: "/pelunasan-dp", 
        label: "Pelunasan DP",
        allowedNames: ["Keuangan"],
        badgeKey: "pelunasan_dp",
    },
     { 
        path: "/pencairan-kpr", 
        label: "Pencairan KPR",
        allowedNames: ["Keuangan"],
        badgeKey: "pencairan_kpr",
    },
    { 
        path: "/pengajuan-transaksi-proyek", 
        label: "Transaksi Proyek", 
        roles: ["PROYEK"],
        allowedNames: ["Proyek"] 
    },
  ]
},

{
  label: "Laporan",
  icon: "bx bx-archive",
  isHasArrow: true,
  groupByDivision: true,
  // "Pengawas Cabang" & "Quality Kontrol" ikut didaftarkan di sini karena
  // penyaring menu induk menolak peran yang tidak terdaftar SEBELUM sub-item
  // dicek — tanpa keduanya, submenu Laporan Quality Control tak terjangkau.
  roles: [ "ADMIN", "Dirut", "Head","Admin Cabang","Admin Kantor Pusat","Pengawas Cabang","Quality Kontrol","Relationship Officer"],
  subItems: [
    // Laporan QC hanya untuk pengawas yang menginput, Kacab (Admin Cabang /
    // Head Branch, dibatasi ke wilayahnya oleh backend), Head Perencanaan,
    // dan Dirut. Head divisi lain dan ADMIN tidak melihat menu ini. Dua baris
    // dipakai karena penyaring menu hanya bisa membatasi level, sedangkan Head
    // perlu dibatasi lagi ke divisinya lewat allowedNames.
    { path: "/laporan-qc", label: "Quality Control", roles: ["Dirut", "Pengawas Cabang", "Quality Kontrol", "Admin Cabang"] },
    { path: "/laporan-qc", label: "Quality Control", roles: ["Head"], allowedNames: ["Perencanaan", "Branch"] },
    // Hutang User (sisa DP): RO melihat unit miliknya, Kacab dibatasi
    // wilayahnya, Head Marketing dan Dirut melihat semua. Pembatasan data
    // ditegakkan di backend berdasarkan JWT.
    { path: "/laporan-hutang-user", label: "Hutang User", roles: ["Dirut", "Admin Cabang", "Relationship Officer"] },
    { path: "/laporan-hutang-user", label: "Hutang User", roles: ["Head"], allowedNames: ["Marketing", "Branch"] },
    // Laporan STK: Head Pemasaran (Marketing), Kacab (Admin Cabang/Head
    // Branch, dibatasi wilayahnya oleh backend), dan Dirut (semua lokasi).
    { path: "/laporan-serah-terima-kunci", label: "Serah Terima Kunci", roles: ["Dirut", "ADMIN", "Pengawas Cabang"] },
    { path: "/laporan-serah-terima-kunci", label: "Serah Terima Kunci", roles: ["Admin Cabang"] },
    { path: "/laporan-serah-terima-kunci", label: "Serah Terima Kunci", roles: ["Head"], allowedNames: ["Marketing", "Branch"] },
    { path: "/neraca", label: "Neraca", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/laba-rugi", label: "Laba Rugi", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/cash-flow", label: "Cash Flow", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Keuangan","HRGA", "Branch", "Legalitas","Perencanaan","Admin Cabang","Admin Kantor Pusat"] },
    { path: "/arus-kas-bank", label: "Kas Bank", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/arus-kas-tunai", label: "Kas Tunai", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/kas-bank-blokir", label: "Kas Bank Blokir", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/hutang-bank", label: "Hutang Bank", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/piutang-karyawan", label: "Piutang Karyawan", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan","HRGA"] },
    { path: "/piutang-pihak-ketiga", label: "Piutang Pihak ketiga", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan","HRGA"] },
    { path: "/deposito", label: "Deposito", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan"] },
    { path: "/hutang-tanah", label: "Hutang Tanah", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan","Legalitas"] },
    { path: "/hutang-mandor", label: "Hutang Mandor", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Keuangan","Branch","Admin Cabang","Admin Kantor Pusat"] },
    { path: "/persediaan-tanah", label: "Persediaan Tanah", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Keuangan","Legalitas","Branch","Admin Cabang","Admin Kantor Pusat"] },
    { path: "/persediaan-bangunan", label: "Persediaan Bangunan", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Keuangan","Legalitas","Branch","Admin Cabang","Admin Kantor Pusat"] },
    { path: "/laporan-pembelian-tanah", label: "Pembelian Tanah", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["Keuangan","Legalitas"] },
    { path: "/laporan-pembangunan-rumah", label: "Pembangunan Rumah", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Branch","Admin Cabang","Admin Kantor Pusat"] },
    { path: "/laporan-tanah-bangunan", label: "Tanah dan Bangunan", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["HRGA"] },
    { path: "/laporan-kendaraan", label: "Kendaraan", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["HRGA"] },
    { path: "/laporan-inventaris-kantor", label: "Inventaris Kantor", roles: ["Head", "ADMIN", "Dirut"], allowedNames: ["HRGA"] },
    { path: "/laporan-dajam", label: "Dajam", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Branch", "Marketing", "Keuangan","Admin Cabang","Admin Kantor Pusat"] },
    { path: "/laporan-stok", label: "Stok", roles: ["Head", "ADMIN", "Dirut","Admin Cabang","Admin Kantor Pusat"], allowedNames: ["Branch", "Marketing","Admin Cabang","Admin Kantor Pusat"] },
        { 
        path: "/rekap-kinerja-marketing", 
        label: "Rekap Kinerja Marketing",
        allowedNames: ["Marketing",  "Dirut", "Admin Kantor Pusat"],
        allowedRoles: ["Dirut", "Head", "Marketing Officer","Admin Kantor Pusat"],
    },
 
  ]
},

    // --- ADMINISTRASI TITLE ---
    {
      isTitle: true,
      title: "Administrasi",
      allowedNames: ["Dirut"] ,
    },
    // --- PENGATURAN (KONSOLIDASI) ---
    {
      label: "Manajemen Pengguna",
      icon: "bx bx-user",
      isHasArrow: true,
      roles: ["Dirut", "ADMIN"],
      subItems: [
        { path: "/user", label: "Daftar Pengguna" },
      ]
    },
    // Panduan bisa dibuka semua role, termasuk Mandor.
    {
      label: "Panduan",
      icon: "bx bx-book-open",
      path: "/panduan",
    },
];

// ==========================================================
// DIMULAI FUNGSI KOMPONEN REACT
// ==========================================================

const SidebarContent = props => {
  const ref = useRef();
  const { pathname } = useLocation();
  const [userLevel, setUserLevel] = useState(null); 
  // Dihitung langsung dari token (bukan state) supaya sudah benar sejak
  // render pertama — penting agar pemanggilan badge tidak terlanjur jalan.
  // Termasuk level "Mandor" walau penautan mandornya kosong (default aman).
  const isMandor = isAkunMandor();
  const [userDivisi, setUserDivisi] = useState(null); 
  const [openMenuLabel, setOpenMenuLabel] = useState(null); 
  const [badgeCounts, setBadgeCounts] = useState({}); // 🆕 { booking_unit: 3, ... }
  const navigate = useNavigate();

  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.removeItem("authUser"); 
    navigate("/login");
    window.location.reload();
  };

  useEffect(() => {
    const authInfo = getUserLevel(); 
    if (authInfo && typeof authInfo === 'object') {
        const { level, divisi } = authInfo; 
        setUserLevel(level); 
        setUserDivisi(divisi); 
        console.log("=================================================");
        console.log("Level Pengguna Aktif:", level);
        console.log("Nama Divisi Aktif (dari JWT):", divisi); 
        console.log("=================================================");
    } else {
        setUserLevel(null);
        setUserDivisi(null);
    }
  }, []);

  // 🆕 Helper: ambil angka total dari berbagai kemungkinan bentuk response
  // (mis. { data: 5 }, { data: { total: 5 } }, { total: 5 }, atau angka langsung)
  const extractCount = (res) => {
    if (res === null || res === undefined) return 0;
    if (typeof res === 'number') return res;
    if (typeof res?.data === 'number') return res.data;
    if (typeof res?.total === 'number') return res.total;
    if (typeof res?.data?.total === 'number') return res.data.total;
    if (typeof res?.meta?.total === 'number') return res.meta.total;
    return 0;
  };

  // 🆕 Ambil jumlah data "belum di-approve" untuk Transaksi, Booking Unit, Pelunasan DP, dan Pencairan KPR
  const fetchAllBadges = useCallback(async () => {
    // Akun mandor tidak berhak atas data keuangan/inbox — jangan dipanggil.
    if (isMandor) return;
    // Badge Transaksi hanya relevan untuk Keuangan / Dirut (sama seperti ApprovalPopup.jsx)
    const levelUpper = String(userLevel || '').toUpperCase();
    const divisiUpper = String(userDivisi || '').toUpperCase();
    const canSeeTransaksiBadge =
      divisiUpper === 'KEUANGAN' ||
      ['DIRUT', 'DIREKTUR', 'DIREKSI'].includes(levelUpper) ||
      divisiUpper === 'DIREKSI';

    // Badge jumlah pengajuan Produk Rumah hanya untuk Direktur.
    // Admin/Kepala Cabang tetap bisa membuka tab pengajuan di halamannya,
    // tetapi tidak menampilkan angka di sidebar.
    const canSeeProdukBadge = ['DIRUT', 'DIREKTUR', 'DIREKSI'].includes(
      levelUpper
    );

    const results = await Promise.allSettled([
      getTotalAjukanKeuangan(),
      getTotalPelunasanDP(),
      getTotalPencairanKPR(),
      canSeeTransaksiBadge
        ? fetchInbox({ per_page: 50 })
        : Promise.resolve(null),
      // "Siap Dicairkan" (status APPROVED) hanya relevan untuk divisi Keuangan
      canSeeTransaksiBadge && divisiUpper === 'KEUANGAN'
        ? fetchPengajuans({ page: 1, per_page: 20 })
        : Promise.resolve(null),
      canSeeProdukBadge
        ? fetchPendingProdukRumah({ page: 1, per_page: 1, status: 'PENDING' })
        : Promise.resolve(null),
    ]);

    const [
      resBooking,
      resPelunasan,
      resPencairan,
      resInbox,
      resPengajuans,
      resProdukRumah,
    ] = results;

    setBadgeCounts(prev => {
      let transaksiCount = prev.transaksi;

      if (resInbox.status === 'fulfilled' || resPengajuans.status === 'fulfilled') {
        // Antrean Persetujuan: item inbox berstatus DIAJUKAN / APPROVED
        const dataInbox = resInbox.status === 'fulfilled' ? (resInbox.value?.data || []) : [];
        const antreanSetuju = dataInbox.filter(item =>
          ['DIAJUKAN', 'APPROVED'].includes(item.status_pengajuan)
        );

        // Siap Dicairkan: item pengajuan (semua) berstatus APPROVED, khusus Keuangan
        const dataPengajuan = resPengajuans.status === 'fulfilled' ? (resPengajuans.value?.data || []) : [];
        const siapCair = dataPengajuan.filter(item => item.status_pengajuan === 'APPROVED');

        transaksiCount = antreanSetuju.length + siapCair.length;
      }

      return {
        ...prev,
        booking_unit: resBooking.status === 'fulfilled' ? extractCount(resBooking.value) : prev.booking_unit,
        pelunasan_dp: resPelunasan.status === 'fulfilled' ? extractCount(resPelunasan.value) : prev.pelunasan_dp,
        pencairan_kpr: resPencairan.status === 'fulfilled' ? extractCount(resPencairan.value) : prev.pencairan_kpr,
        produk_rumah:
          resProdukRumah.status === 'fulfilled'
            ? extractCount(resProdukRumah.value)
            : prev.produk_rumah,
        transaksi: transaksiCount,
      };
    });

    const labels = [
      'Booking Unit',
      'Pelunasan DP',
      'Pencairan KPR',
      'Transaksi (Inbox)',
      'Transaksi (Pengajuan)',
      'Pengajuan Produk Rumah',
    ];
    results.forEach((r, idx) => {
      if (r.status === 'rejected') {
        console.error(`Gagal memuat badge ${labels[idx]}:`, r.reason);
      }
    });
  }, [userLevel, userDivisi]);

  useEffect(() => {
    // Fetch sekali saat sidebar mount, lalu refresh berkala tiap 60 detik.
    fetchAllBadges();
    const interval = setInterval(fetchAllBadges, 60000);

    // Halaman Produk Rumah memicu ini setelah submit/approve/reject agar
    // angka di sidebar langsung berubah tanpa menunggu interval.
    const refreshHandler = () => fetchAllBadges();
    window.addEventListener("asera:refresh-badges", refreshHandler);

    return () => {
      clearInterval(interval);
      window.removeEventListener("asera:refresh-badges", refreshHandler);
    };
  }, [fetchAllBadges]);

  const handleMenuClick = useCallback((e, itemLabel) => {
    e.preventDefault();
    setOpenMenuLabel(currentLabel => 
      currentLabel === itemLabel ? null : itemLabel
    );
  }, []);
  
  const activateParentDropdown = useCallback((item) => {
    const parent = item.parentElement;
    if (parent) {
      parent.classList.add("mm-active");
      let parent2 = parent.parentElement;
      while (parent2 && parent2.tagName !== 'DIV') {
        if (parent2.tagName === 'UL') {
          parent2.classList.add("mm-show"); 
        }
        if (parent2.tagName === 'LI') {
          parent2.classList.add("mm-active"); 
          if (parent2.childNodes.length > 0 && parent2.childNodes[0].tagName === 'A') {
            parent2.childNodes[0].classList.add("mm-active"); 
          }
        }
        parent2 = parent2.parentElement;
      }
    }
  }, []);

  const removeActivation = (items) => {
    for (let i = 0; i < items.length; ++i) {
      let item = items[i];
      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      let parent = items[i].parentElement;
      while (parent && parent.tagName !== 'DIV') {
        parent.classList.remove('mm-active', 'mm-show');
        if (parent.childNodes.length > 0 && parent.childNodes[0].tagName === 'A') {
             parent.childNodes[0].classList.remove("mm-active");
        }
        parent = parent.parentElement;
      }
    }
  };

  const activeMenu = useCallback(() => {
    const ul = document.getElementById("side-menu");
    if (!ul) return;
    const items = ul.getElementsByTagName("a");
    removeActivation(Array.from(items));
    let matchingMenuItem = null;
    const currentPath = pathname.split('?')[0];
    for (let i = 0; i < items.length; ++i) {
      if (items[i] && items[i].pathname) {
        const itemPath = items[i].pathname.split('?')[0];
        if (itemPath === currentPath) {
            matchingMenuItem = items[i];
            break;
        } else if (itemPath.length > 1 && currentPath.startsWith(itemPath)) {
            matchingMenuItem = items[i];
            break;
        }
      }
    }
    if (matchingMenuItem) {
      matchingMenuItem.classList.add("active");
      activateParentDropdown(matchingMenuItem);
    }
  }, [pathname, activateParentDropdown]);

  useEffect(() => {
    if (userLevel) {
        setTimeout(() => {
            activeMenu();
        }, 100);
    }
  }, [pathname, userLevel, activeMenu]); 

  useEffect(() => {
    if (ref.current && ref.current.recalculate) {
        setTimeout(() => {
            ref.current.recalculate();
        }, 50); 
    }
  }, [pathname, userLevel]);

  // ==========================================================
  // 🚨 FUNGSI FILTER BERDASARKAN NAMA DIVISI (STRING) 🚨
  // ==========================================================
  const isAllowedByDivisiName = useCallback((item) => {
    // 🆕 Anggap "tidak ada divisi" untuk semua kemungkinan nilai kosong:
    // null, undefined, maupun string kosong ("" atau spasi saja) dari JWT.
    // Kalau user memang tidak punya divisi, semua item tetap ditampilkan.
    const isDivisiEmpty =
      userDivisi === null ||
      userDivisi === undefined ||
      String(userDivisi).trim() === '';
    if (isDivisiEmpty) return true;

    if (!item.allowedNames || item.allowedNames.length === 0) return true; 
    const userDivisiName = String(userDivisi).toLowerCase();
    const allowedNamesLower = item.allowedNames.map(name => String(name).toLowerCase());
    return allowedNamesLower.includes(userDivisiName);
  }, [userDivisi]);

  // 🆕 Cek apakah sebuah menu induk (yang punya subItems) masih memiliki
  // minimal satu sub-item yang bisa diakses user (berdasarkan role & divisi).
  // Dipakai supaya menu induk (mis. "Master", "Laporan") TIDAK langsung
  // disembunyikan hanya karena divisi user tidak cocok dengan allowedNames
  // di level induk — selama masih ada sub-item generik (tanpa allowedNames,
  // seperti "Jabatan", "Tipe Transaksi", "Alur Approval", dll) yang boleh
  // diakses, menu induk tetap tampil.
  const hasEligibleSubItems = useCallback((item) => {
    if (!item.subItems || item.subItems.length === 0) return false;

    if (userLevel === "Dirut" && item.groupByDivision) {
      return item.subItems.some(sub => {
        const rolesToCheck = sub.allowedRoles || sub.roles;
        return rolesToCheck ? rolesToCheck.includes(userLevel) : true;
      });
    }

    return item.subItems.some(sub => {
      const rolesToCheck = sub.allowedRoles || sub.roles;
      if (rolesToCheck && !rolesToCheck.includes(userLevel)) return false;
      if (!isAllowedByDivisiName(sub)) return false;
      return true;
    });
  }, [userLevel, isAllowedByDivisiName]);

  // 🆕 Komponen kecil untuk badge angka notifikasi di samping label menu
  const NotifBadge = ({ count }) => {
    if (!count || count <= 0) return null;
    return (
      <span
        className="sidebar-notif-badge"
        style={{
          marginLeft: '8px',
          minWidth: '20px',
          height: '20px',
          padding: '0 6px',
          borderRadius: '999px',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #dc3545 100%)',
          color: '#ffffff',
          fontSize: '11px',
          fontWeight: '700',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          boxShadow: '0 2px 5px rgba(220, 53, 69, 0.45)',
          border: '1.5px solid #ffffff',
          flexShrink: 0,
        }}
      >
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  // ==========================================================
  // RENDER JSX DENGAN ENHANCEMENT STYLE
  // ==========================================================
  return (
    <React.Fragment>
      <style>{`
        @keyframes sidebarBadgePulse {
          0% { box-shadow: 0 2px 5px rgba(220, 53, 69, 0.45), 0 0 0 0 rgba(220, 53, 69, 0.5); }
          70% { box-shadow: 0 2px 5px rgba(220, 53, 69, 0.45), 0 0 0 6px rgba(220, 53, 69, 0); }
          100% { box-shadow: 0 2px 5px rgba(220, 53, 69, 0.45), 0 0 0 0 rgba(220, 53, 69, 0); }
        }
        .sidebar-notif-badge {
          animation: sidebarBadgePulse 2.2s ease-out infinite;
        }
      `}</style>
      <SimpleBar 
        className="h-100" 
        ref={ref} 
        style={{ backgroundColor: COLOR_SCHEME.BG_DEFAULT }} 
      >
        <div id="sidebar-menu">
          <ul className="list-unstyled" id="side-menu">
            {MENU_ITEMS.map((item, key) => {
              // 0. Akun mandor: hanya menu pekerjaannya sendiri + panduan.
              if (
                isMandor &&
                item.path !== "/pembangunan-saya" &&
                item.path !== "/panduan"
              ) {
                return null;
              }

              // 1. Filter Level/Role Menu Induk
              if (item.roles && !item.roles.includes(userLevel)) {
                return null;
              }

              // 2. Filter Divisi Menu Induk
              //    - Jika item TIDAK punya subItems (leaf/link tunggal atau title),
              //      filter divisi langsung diterapkan ke item itu sendiri.
              //    - Jika item PUNYA subItems (dropdown, mis. Master/Laporan),
              //      JANGAN langsung disembunyikan berdasarkan allowedNames di
              //      level induk. Biarkan filtering divisi terjadi per sub-item
              //      (lihat isAllowedByDivisiName di bagian render subItems).
              //      Induk hanya disembunyikan kalau memang TIDAK ADA satupun
              //      sub-item yang bisa diakses user.
              if (!item.subItems) {
                if (!isAllowedByDivisiName(item)) {
                  return null;
                }
              } else {
                if (!hasEligibleSubItems(item)) {
                  return null;
                }
              }

              // --- PEMISAH GARIS ---
              if (item.isSeparator) {
                return (
                    <li key={`sep-${key}`} style={{ padding: '5px 20px 5px 20px' }}>
                        <hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: '0' }} />
                    </li>
                );
              }

              const pathWithoutQuery = item.path ? item.path.split('?')[0] : '';
              const currentPath = pathname.split('?')[0];
              const isActive = pathWithoutQuery.length > 1 ? currentPath.startsWith(pathWithoutQuery) : currentPath === pathWithoutQuery;
              const isMenuOpen = item.isHasArrow && openMenuLabel === item.label;

              // --- TITLE/SEPARATOR ---
              if (item.isTitle) {
                return (
                  <li 
                    className="menu-title" 
                    key={`title-${key}`} 
                    style={{ 
                      color: COLOR_SCHEME.MENU_TITLE, 
                      fontSize: '11px', 
                      fontWeight: 'bold',
                      letterSpacing: '0.5px', 
                      padding: '20px 0 5px 20px',
                      textTransform: 'uppercase', 
                      margin: '10px 0 5px 0'
                    }}
                  >
                    {props.t(item.title)}
                  </li>
                );
              }

              // --- MENU ITEM NORMAL/HAS ARROW ---
              return (
                <li 
                    key={`menu-item-${key}`}
                    className={isMenuOpen ? "mm-active" : ""} 
                >
                  <Link
                    to={item.path || "#"}
                    className={item.isHasArrow ? "has-arrow" : ""}
                    data-tour-top="1"
                    data-tour-id={item.path || `menu-${item.label}`}
                    data-tour-label={item.label}
                    onClick={item.isHasArrow ? (e) => handleMenuClick(e, item.label) : undefined}
                    style={{ 
                      color: isActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.TEXT_DEFAULT,
                      fontWeight: isActive ? '600' : 'normal',
                      padding: '10px 15px', 
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '8px', 
                      margin: '5px 15px',
                      transition: 'all 0.3s',
                      backgroundColor: isActive ? COLOR_SCHEME.ACTIVE_BG : 'transparent',
                      boxShadow: isActive ? `0 2px 5px ${COLOR_SCHEME.SHADOW_COLOR}` : 'none',
                    }}
                  >
                    <i 
                        className={item.icon} 
                        style={{ 
                            color: isActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.ICON_COLOR,
                            fontSize: '18px',
                            marginRight: '12px'
                        }}
                    ></i>
                    <span style={{ flexGrow: 1 }}>{props.t(item.label)}</span>
                  </Link>
                  
                  {/* --- SUB-MENU (Tingkat 2) --- */}
                  {item.subItems && (
                    <ul 
                      className="sub-menu" 
                      style={{ 
                          maxHeight: isMenuOpen ? '5000px' : '0', 
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease-out',
                          backgroundColor: COLOR_SCHEME.SUB_MENU_BG,
                          borderRadius: '8px', 
                          margin: '0 15px 5px 15px',
                          padding: '5px 0',
                      }}
                    >
                      {/* 🆕 Jika Dirut & menu dengan groupByDivision, tampilkan per divisi */}
                      {userLevel === "Dirut" && item.groupByDivision ? (
                        (() => {
                          // Sub-item yang lolos filter role (Dirut lolos semua)
                          const eligibleSubs = item.subItems.filter(sub => {
                            const rolesToCheck = sub.allowedRoles || sub.roles;
                            return rolesToCheck ? rolesToCheck.includes(userLevel) : true;
                          });

                          // Pisahkan item dengan dan tanpa divisi
                          const subsWithDiv = eligibleSubs.filter(sub => 
                            sub.allowedNames && sub.allowedNames.length > 0
                          );
                          const subsWithoutDiv = eligibleSubs.filter(sub => 
                            !sub.allowedNames || sub.allowedNames.length === 0
                          );

                          // Kumpulkan SEMUA nama divisi unik dari semua allowedNames
                          const allDivisions = [...new Set(
                            subsWithDiv.flatMap(sub => sub.allowedNames)
                          )];

                          return (
                            <>
                              {/* Tampilkan grup per divisi */}
                              {allDivisions.map(divName => {
                                const divItems = subsWithDiv.filter(sub =>
                                  sub.allowedNames.includes(divName)
                                );
                                if (divItems.length === 0) return null;

                                return (
                                  <React.Fragment key={`div-${divName}`}>
                                    <li className="menu-title" style={{
                                      color: COLOR_SCHEME.PRIMARY_COLOR,
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      padding: '12px 15px 5px 35px',
                                      textTransform: 'uppercase',
                                    }}>
                                      {divName}
                                    </li>
                                    {divItems.map((subItem, idx) => {
                                      const subItemPath = subItem.path.split('?')[0];
                                      const isSubActive = currentPath === subItemPath;
                                      const count = subItem.badgeKey ? badgeCounts[subItem.badgeKey] : null;
                                      return (
                                        <li key={`sub-item-${divName}-${idx}`}>
                                          <Link
                                            to={subItem.path}
                                            style={{
                                              color: isSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.TEXT_DEFAULT,
                                              padding: '8px 15px 8px 50px',
                                              fontWeight: isSubActive ? '600' : 'normal',
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <span style={{ marginRight: '8px', fontSize: '10px', color: isSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.ICON_COLOR }}>•</span>
                                            <span style={{ flexGrow: 1 }}>{props.t(subItem.label)}</span>
                                            <NotifBadge count={count} />
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}

                              {/* Tampilkan item tanpa divisi (Umum) jika ada */}
                              {subsWithoutDiv.length > 0 && (
                                <React.Fragment>
                                  <li className="menu-title" style={{
                                    color: COLOR_SCHEME.PRIMARY_COLOR,
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    padding: '12px 15px 5px 35px',
                                    textTransform: 'uppercase',
                                  }}>
                                    Umum
                                  </li>
                                  {subsWithoutDiv.map((subItem, idx) => {
                                    const subItemPath = subItem.path.split('?')[0];
                                    const isSubActive = currentPath === subItemPath;
                                    const count = subItem.badgeKey ? badgeCounts[subItem.badgeKey] : null;
                                    return (
                                      <li key={`sub-item-umum-${idx}`}>
                                        <Link
                                          to={subItem.path}
                                          style={{
                                            color: isSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.TEXT_DEFAULT,
                                            padding: '8px 15px 8px 50px',
                                            fontWeight: isSubActive ? '600' : 'normal',
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <span style={{ marginRight: '8px', fontSize: '10px', color: isSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.ICON_COLOR }}>•</span>
                                          <span style={{ flexGrow: 1 }}>{props.t(subItem.label)}</span>
                                          <NotifBadge count={count} />
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </React.Fragment>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        // ➡️ Tampilan normal untuk selain Dirut
                        item.subItems.map((subItem, subKey) => {
                          const rolesToCheck = subItem.allowedRoles || subItem.roles;
                          if (rolesToCheck && !rolesToCheck.includes(userLevel)) return null;
                          if (!isAllowedByDivisiName(subItem)) return null;

                          // Sub-menu tingkat 3
                          if (!subItem.path && subItem.subItems && subItem.isHasArrow) {
                            const subMenuLabelKey = `${item.label}-${subItem.label}`;
                            const isSubMenuOpen = openMenuLabel === subMenuLabelKey;

                            return (
                              <li 
                                key={`sub-item-group-${key}-${subKey}`}
                                className={isSubMenuOpen ? "mm-active" : ""}
                              >
                                <Link 
                                  to="#" 
                                  className="has-arrow" 
                                  onClick={(e) => handleMenuClick(e, subMenuLabelKey)}
                                  style={{ 
                                    color: COLOR_SCHEME.TEXT_DEFAULT, 
                                    padding: '8px 15px 8px 35px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {props.t(subItem.label)}
                                </Link>
                                <ul 
                                  className="sub-menu" 
                                  style={{ 
                                    maxHeight: isSubMenuOpen ? '5000px' : '0', 
                                    overflow: 'hidden',
                                    transition: 'max-height 0.3s ease-out',
                                    backgroundColor: '#f1f3f7',
                                    borderRadius: '8px',
                                    margin: '5px 0',
                                  }}
                                >
                                  {subItem.subItems.map((subSubItem, subSubKey) => {
                                    if (subSubItem.roles && !subSubItem.roles.includes(userLevel)) return null;
                                    if (!isAllowedByDivisiName(subSubItem)) return null;
                                    const subSubItemPath = subSubItem.path ? subSubItem.path.split('?')[0] : '';
                                    const isSubSubActive = currentPath === subSubItemPath;
                                    if (!subSubItem.path) return null;
                                    return (
                                      <li key={`sub-sub-item-${key}-${subKey}-${subSubKey}`}>
                                        <Link 
                                          to={subSubItem.path}
                                          style={{ 
                                            color: isSubSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.TEXT_DEFAULT,
                                            padding: '8px 15px 8px 50px',
                                            fontWeight: isSubSubActive ? '600' : 'normal',
                                          }}
                                        >
                                          <span style={{ marginRight: '8px', fontSize: '10px', color: isSubSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.ICON_COLOR }}>•</span>
                                          {props.t(subSubItem.label)}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </li>
                            );
                          }

                          // Link tingkat 2 biasa
                          const subItemPath = subItem.path.split('?')[0];
                          const isSubActive = currentPath === subItemPath;
                          const count = subItem.badgeKey ? badgeCounts[subItem.badgeKey] : null;
                          return (
                            <li key={`sub-item-${key}-${subKey}`}>
                              <Link 
                                to={subItem.path} 
                                style={{ 
                                  color: isSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.TEXT_DEFAULT,
                                  padding: '8px 15px 8px 35px',
                                  fontWeight: isSubActive ? '600' : 'normal',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <span style={{ marginRight: '8px', fontSize: '10px', color: isSubActive ? COLOR_SCHEME.ACTIVE_TEXT : COLOR_SCHEME.ICON_COLOR }}>•</span>
                                <span style={{ flexGrow: 1 }}>{props.t(subItem.label)}</span>
                                <NotifBadge count={count} />
                              </Link>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          <ul className="list-unstyled" style={{ marginBottom: '20px', paddingBottom: '20px' }}>
            <li><hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: '10px 15px' }} /></li>
            <li>
              <Link 
                to="#" 
                onClick={handleLogout}
                style={{ 
                  color: COLOR_SCHEME.DANGER_TEXT, 
                  padding: '12px 15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  borderRadius: '8px', 
                  margin: '0 15px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLOR_SCHEME.DANGER_BG}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <i className="bx bx-log-out" style={{ fontSize: '20px', marginRight: '12px' }}></i>
                <span style={{ fontWeight: 'bold' }}>{props.t("Logout")}</span>
              </Link>
            </li>
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));